import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine } from "@/components/Reveal";
import { waLink } from "@/lib/api";

const HERO_BG =
  "https://customer-assets-jai6qajn.emergentagent.net/job_elixz-occasions/artifacts/7ps97q08_gfx_ge_create_a_chocolates_theme_background_theme_with_some_cus_3c0ed749-8470-40bb-ace8-fd9ebc72740f.webp";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollDown = () => {
    const el = document.getElementById("story");
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -60 });
  };

  return (
    <section
      id="hero"
      ref={ref}
      className="relative flex min-h-screen items-center overflow-hidden"
      data-testid="hero"
    >
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 -z-10"
      >
        <img
          src={HERO_BG}
          alt="Elixz artisan chocolates"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0F0616]/72" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F0616] via-transparent to-[#0F0616]/60" />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="mx-auto w-full max-w-[1400px] px-6 pt-32 md:px-12"
      >
        <div className="max-w-3xl rounded-3xl bg-[#440089]/75 p-8 backdrop-blur-md md:p-12">
        <MaskedLine delay={0.35}>
          <span className="text-xs uppercase tracking-[0.4em] text-gold">
            Elixz Chocolates &middot; PCMC, Pune
          </span>
        </MaskedLine>

        <h1 className="mt-6 font-serif font-light leading-[0.92] tracking-tight text-[#FDFBF7]">
          <MaskedLine delay={0.5} className="text-5xl sm:text-7xl lg:text-8xl">
            Every occasion
          </MaskedLine>
          <MaskedLine delay={0.66} className="text-5xl sm:text-7xl lg:text-8xl">
            deserves a
          </MaskedLine>
          <MaskedLine
            delay={0.82}
            className="gold-gradient-text text-6xl italic sm:text-8xl lg:text-9xl"
          >
            Gift of Elixz
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-[#EAD9B0] md:text-lg"
        >
          Handcrafted chocolate bouquets, bobo balloon surprises and bespoke
          hampers — beautifully presented for weddings, corporate events and
          every milestone worth celebrating.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() =>
              document.getElementById("collection")?.scrollIntoView({
                behavior: "smooth",
              })
            }
            data-testid="hero-explore-btn"
            className="rounded-full bg-gold px-8 py-4 text-sm font-semibold text-[#0F0616] transition-transform duration-300 hover:scale-[1.04] active:scale-95"
          >
            Explore the Collection
          </button>
          <a
            href={waLink("Hi Elixz! I'd like to discuss a gifting order.")}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-whatsapp-btn"
            className="rounded-full border border-[#D4AF37]/50 px-8 py-4 text-sm font-semibold text-[#FDFBF7] transition-colors duration-300 hover:bg-[#D4AF37]/10"
          >
            Enquire for Events
          </a>
        </motion.div>
        </div>
      </motion.div>

      <motion.button
        onClick={scrollDown}
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] text-[#A894C2]"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        data-testid="hero-scroll"
      >
        Scroll
      </motion.button>
    </section>
  );
}
